/**
 * Cart Store — Persisted cart state with localStorage.
 *
 * Replaces the volatile React context. Cart survives page refreshes.
 * In Phase 3, this will also sync with the database for logged-in users.
 */

import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import { products, type Product } from "@/lib/products";

// ── Types ────────────────────────────────────────────────────

export type CartLine = {
  product: Product;
  qty: number;
  /** Selected variant (e.g., pot finish) */
  variant?: string;
};

type CartState = {
  lines: CartLine[];
  open: boolean;
  setOpen: (v: boolean) => void;
  add: (product: Product, qty?: number, variant?: string) => void;
  setQty: (productId: string, qty: number) => void;
  remove: (productId: string) => void;
  clear: () => void;
  count: number;
  subtotal: number;
};

// ── Storage ──────────────────────────────────────────────────

const CART_KEY = "verdant-cart";

function loadCart(): CartLine[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(CART_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CartLine[];
    // Auto-migrate & synchronize cart lines with authoritative catalog prices
    return parsed.map((line) => {
      const freshProduct = products.find(
        (p) => p.id === line.product?.id || (line.product?.slug && p.slug === line.product?.slug)
      );
      if (freshProduct) {
        return {
          ...line,
          product: {
            ...line.product,
            price: freshProduct.price,
            name: freshProduct.name,
            image: freshProduct.image || line.product.image,
            inStock: freshProduct.inStock,
            stockQuantity: freshProduct.stockQuantity,
          },
        };
      }
      return line;
    });
  } catch {
    return [];
  }
}

function saveCart(lines: CartLine[]) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(CART_KEY, JSON.stringify(lines));
  } catch {
    // Storage full or private browsing — silently fail
  }
}

// ── Context ──────────────────────────────────────────────────

const CartContext = createContext<CartState | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>([]);
  const [open, setOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate from localStorage on mount (client only)
  useEffect(() => {
    setLines(loadCart());
    setHydrated(true);
  }, []);

  // Persist to localStorage on change
  useEffect(() => {
    if (hydrated) {
      saveCart(lines);
    }
  }, [lines, hydrated]);

  const add = useCallback((product: Product, qty = 1, variant?: string, openDrawer = true) => {
    const freshProduct =
      products.find((p) => p.id === product.id || (product.slug && p.slug === product.slug)) ||
      product;

    // Check if item is out of stock
    const rawStock = (freshProduct as { stock?: number }).stock;
    const isOutOfStock =
      freshProduct.inStock === false ||
      (freshProduct.stockQuantity !== undefined && freshProduct.stockQuantity <= 0) ||
      (rawStock !== undefined && rawStock <= 0);

    if (isOutOfStock) {
      if (typeof window !== "undefined") {
        alert(`"${freshProduct.name}" is currently out of stock.`);
      }
      return;
    }

    setLines((prev) => {
      const found = prev.find((l) => l.product.id === freshProduct.id);
      if (found) {
        return prev.map((l) =>
          l.product.id === freshProduct.id
            ? { ...l, product: freshProduct, qty: l.qty + qty, variant: variant ?? l.variant }
            : l,
        );
      }
      return [...prev, { product: freshProduct, qty, variant }];
    });
    if (openDrawer) {
      setOpen(true);
    }
  }, []);

  const setQty = useCallback((productId: string, qty: number) => {
    setLines((prev) =>
      qty <= 0
        ? prev.filter((l) => l.product.id !== productId)
        : prev.map((l) => (l.product.id === productId ? { ...l, qty } : l)),
    );
  }, []);

  const remove = useCallback((productId: string) => {
    setLines((prev) => prev.filter((l) => l.product.id !== productId));
  }, []);

  const clear = useCallback(() => {
    setLines([]);
  }, []);

  const value = useMemo<CartState>(
    () => ({
      lines,
      open,
      setOpen,
      add,
      setQty,
      remove,
      clear,
      count: lines.reduce((n, l) => n + l.qty, 0),
      subtotal: lines.reduce((n, l) => n + l.qty * l.product.price, 0),
    }),
    [lines, open, add, setQty, remove, clear],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartState {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
